import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * PDP upsell checkbox for the recommended anti-slip underlay. When ticked, the underlay rides
 * along in the product form's own /cart/add.js, so one click makes exactly one request.
 *
 * The underlay goes in as a NESTED cart line via `parent_id`, which Shopify honours all the way
 * through checkout and order emails: it orders the underlay directly under its rug and removes
 * it when the rug goes. `parent_id` takes the parent's VARIANT id and only works when that
 * variant is added in the same request — exactly this case. Shopify rejects it for a parent line
 * that already exists in the cart; the in-cart card uses `parent_line_key` for that instead.
 *
 * Contributed with `skipBundleId`, so product-form.js leaves `_apa_bundle_id` off both lines.
 * That keeps the apa cart transform out of it: bundled lines get merged, and a merged line is
 * not eligible for the line-level discount that prices this add-on. (Shopify also refuses to
 * merge nested lines, so the transform could not touch them anyway — belt and braces.)
 *
 * @typedef {object} ApcUpsellRefs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} [liveRegion]
 *
 * @extends Component<ApcUpsellRefs>
 */
class ApcUpsell extends Component {
  // The card (and its checkbox) is absent whenever the current variant offers no add-on, so
  // nothing here is a required ref. The checkbox is read live via `#checkbox()` when present.
  #abortController = new AbortController();
  #selected = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    // Contribute into the product form's single add request.
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.cartAddCollect, this.#onCollect, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // The offered underlay comes from the selected variant's metafield, so re-render from
    // fresh server markup whenever the shopper switches variant.
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Queried live rather than read off `this.refs`: the base class refreshes refs from a
   * MutationObserver, which is async, so refs are still the detached old input on the line
   * after an innerHTML swap.
   * @returns {HTMLInputElement | null}
   */
  #checkbox() {
    const checkbox = this.querySelector('.checkbox__input');
    return checkbox instanceof HTMLInputElement ? checkbox : null;
  }

  /**
   * Called by the checkbox's native `change` event (declarative `on:change`).
   * @param {{ target: EventTarget | null }} event
   */
  handleToggle(event) {
    const checkbox = event.target;
    if (checkbox instanceof HTMLInputElement) this.#selected = checkbox.checked;
  }

  /**
   * The label's `for` toggles the checkbox on any click inside it, including the info
   * popover's button and the dialog it opens. Swallow those so the popover can be used
   * without also (de)selecting the add-on.
   * @param {MouseEvent} event
   */
  handleCardClick(event) {
    const target = /** @type {HTMLElement | null} */ (event.composedPath?.()[0] ?? event.target);
    if (target?.closest('button, a[href], dialog')) {
      event.preventDefault();
    }
  }

  /**
   * Hand the underlay to the product form so it goes in the same /cart/add.js as the rug.
   * @param {Event} event
   */
  #onCollect = (event) => {
    if (!this.#selected) return;

    const variantId = this.dataset.upsellVariantId;
    const parentVariantId = this.dataset.parentVariantId;
    // Without a parent there is nothing to nest under, and an orphan underlay line would
    // never be discounted — skip rather than add it at full price.
    if (!variantId || !parentVariantId) return;

    const detail = /** @type {{ items: Array<object>, parentQuantity?: number }} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    detail.items.push({
      id: Number(variantId),
      // One pad per rug, so the underlay mirrors the rug's quantity.
      quantity: detail.parentQuantity || 1,
      parent_id: Number(parentVariantId),
      // Stay a standalone cart line so the discount function can still price it.
      skipBundleId: true,
      // Brand rides along as a line item property so cart-sourced GA4 events
      // report the consumer-facing brand instead of the vendor (supplier).
      ...(this.dataset.upsellBrand ? { properties: { _apc_brand: this.dataset.upsellBrand } } : {}),
    });
  };

  /**
   * Reset the tick once the cart confirms the add went through.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!this.#selected) return;

    const data = /** @type {any} */ (event).detail?.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;
    if (data.productId !== this.dataset.productId) return;

    this.#selected = false;
    const checkbox = this.#checkbox();
    if (checkbox) checkbox.checked = false;
    this.#announce();
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { html } = event.detail.data;
    const sectionId = this.closest('.shopify-section')?.id;
    const scope = (sectionId && html.querySelector(`#${CSS.escape(sectionId)}`)) || html;
    // Match this block's own card in the fresh markup, not the first `apc-upsell` in the
    // section: a second block would otherwise copy the first card's variant ids and product.
    const blockId = this.dataset.blockId;
    const replacement = blockId
      ? scope.querySelector(`apc-upsell[data-block-id="${CSS.escape(blockId)}"]`)
      : scope.querySelector('apc-upsell');

    // The block always renders an `<apc-upsell>`, so a missing counterpart means the section
    // markup didn't include this block at all — leave the current card untouched.
    if (!replacement) return;

    // Mirror the fresh server render for the newly selected variant. The element is empty
    // (no card) whenever that variant offers no add-on.
    this.innerHTML = replacement.innerHTML;

    const upsellVariantId = replacement.dataset.upsellVariantId;
    if (upsellVariantId) {
      this.hidden = false;
      this.dataset.parentVariantId = replacement.dataset.parentVariantId;
      this.dataset.upsellVariantId = upsellVariantId;
      if (replacement.dataset.upsellBrand) {
        this.dataset.upsellBrand = replacement.dataset.upsellBrand;
      } else {
        delete this.dataset.upsellBrand;
      }

      // Fresh markup is unchecked; restore whatever the shopper had picked.
      const checkbox = this.#checkbox();
      if (checkbox) checkbox.checked = this.#selected;
    } else {
      // No add-on for this variant. Drop the selection and the stale variant ids too, or a
      // later add would attach the previous variant's underlay to this one.
      this.hidden = true;
      this.#selected = false;
      delete this.dataset.upsellVariantId;
      delete this.dataset.parentVariantId;
      delete this.dataset.upsellBrand;
    }
  };

  #announce() {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;
    liveRegion.textContent = Theme?.translations?.added || 'Added';
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('apc-upsell')) {
  customElements.define('apc-upsell', ApcUpsell);
}
